import { tooManyRequests, unauthorized, failure } from '../types/api';
import { InsertToLocalStorage } from '../utility/localStorage';
import { UnauthorizedErrorKey } from '../types/localStorage';
import { InactivityErrorMessage } from '../types/errorMessage';

const apiUrl = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const handleUnauthorized = () => {
  sessionStorage.removeItem('SessionToken');
  sessionStorage.removeItem('User');
  InsertToLocalStorage(UnauthorizedErrorKey, InactivityErrorMessage);
  window.location.href = '/login';
};

const readErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const body = (await response.json()) as Record<string, unknown>;
    const message = body.detail ?? body.message ?? body.title;
    if (typeof message === 'string' && message.length > 0) return message;
  } else {
    const body = await response.text();
    if (body.length > 0) return body;
  }

  return `Request failed with status ${response.status}.`;
};

async function apiRequest<T>(
  method: 'DELETE' | 'GET' | 'POST' | 'PUT',
  endpoint: string,
  params?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const requestHeaders = { 'Content-Type': 'application/json' };

  if (sessionStorage.getItem('SessionToken') != null) {
    Object.assign(requestHeaders, { 'X-Session-Token': sessionStorage.getItem('SessionToken') });
  }

  const response = await fetch(apiUrl + '/' + endpoint, {
    headers: requestHeaders,
    body: method === 'GET' || method === 'DELETE' ? undefined : JSON.stringify(params ?? {}),
    method,
    ...(signal ? { signal } : {}),
  });

  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new ApiError(await readErrorMessage(response), response.status);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

async function apiPost(endpoint: string, params?: any, signal?: AbortSignal): Promise<any> {
  try {
    return (await apiRequest<unknown>('POST', endpoint, params, signal)) ?? '';
  } catch (exc) {
    if (exc instanceof ApiError) {
      if (exc.status === 429) return tooManyRequests;
      if (exc.status === 401) return unauthorized;
      if (exc.status >= 400 && exc.status < 600) return failure;
    }

    console.log(exc);
    throw failure;
  }
}

const apiFunctions = {
  apiDelete: (endpoint: string) => apiRequest<void>('DELETE', endpoint),
  apiGet: <T>(endpoint: string) => apiRequest<T>('GET', endpoint),
  apiPost,
  apiPostRequest: <T>(endpoint: string, params: unknown) => apiRequest<T>('POST', endpoint, params),
  apiPut: <T>(endpoint: string, params: unknown) => apiRequest<T>('PUT', endpoint, params),
};

export default apiFunctions;
